/**
 * AI CONTEXT: component - Admin SMS Bulk Messaging Modal
 * Journal: J-20250917-01
 * 
 * Purpose:
 * - Allow admins to send bulk SMS messages to selected users
 * - Provide message composition interface with compliance features
 * - Show recipient count and opt-in status
 * - Handle message formatting and delivery tracking
 * 
 * Features:
 * - Message composition with character count
 * - Compliance text auto-append
 * - Recipient validation and filtering
 * - Delivery status tracking
 * - Error handling and user feedback
 */

import React, { useState, useEffect } from 'react';
import { X, MessageSquare, Users, AlertCircle, CheckCircle, Send, RefreshCw } from 'lucide-react';
import { sendBulkSms, formatAdminBulkMessage } from '../services/smsService';
import { getUserProfile, authorizedFetch } from '../services/apiClient';
import { uploadAdminSmsImage, getImageUrlFromKey, isStorageConfigured } from '../services/storage';

const buildSmsPreviewUrlFromKey = (key) => {
  const smsPreviewBase =
    process.env.REACT_APP_SMS_PREVIEW_BASE_URL ||
    'https://sms.tolberttactical.com';

  const toBase64Url = (s) => {
    // key is ASCII; btoa is safe here
    const b64 = btoa(s);
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  };

  const secureId = toBase64Url(key);
  const extMatch = key.toLowerCase().match(/\.(jpe?g|png|gif|webp)$/);
  const ext = extMatch ? extMatch[1] : 'jpg';
  const labelNum = Math.floor(1000 + Math.random() * 9000); // cosmetic only

  return `${smsPreviewBase.replace(/\/+$/g, '')}/secure/message${labelNum}-${secureId}.${ext}`;
};

const AdminSmsModal = ({ isOpen, onClose, selectedUsers, allUsers }) => {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);
  const [error, setError] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [sendAsMms, setSendAsMms] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageRef, setImageRef] = useState(null);
  const [imageUrl, setImageUrl] = useState(null); // direct image URL for in-modal preview
  const [imageLinkUrl, setImageLinkUrl] = useState(null); // preview-page URL for SMS link previews
  const [imageName, setImageName] = useState('');

  // Function to get fresh user data without cache
  const getFreshUserData = async (email) => {
    try {
      console.log(`🔍 Debug: Getting fresh user data for ${email}...`);
      // Use a reasonable batch size to find the user
      const response = await authorizedFetch(`/admin/users?limit=25&offset=0&cache=false`, { method: 'GET' });
      if (response && response.users && response.users.length > 0) {
        // Find the specific user by email
        const user = response.users.find(u => u.email === email);
        if (user) {
          console.log(`🔍 Debug: Fresh user data for ${email}:`, user);
          return user;
        }
      }
      return null;
    } catch (error) {
      console.error(`❌ Failed to get fresh user data for ${email}:`, error);
      return null;
    }
  };

  // Calculate recipients when modal opens or selection changes
  useEffect(() => {
    const loadUserProfilesAndCheckSmsConsent = async () => {
      if (isOpen) {
        setLoadingProfiles(true);
        setError(null);
        
        try {
          let usersToProcess;
          
          if (selectedUsers.size > 0) {
            // If specific users are selected, use only those
            usersToProcess = allUsers.filter(user => selectedUsers.has(user.email));
          } else {
            // If no specific selection (select all), fetch ALL users from backend
            console.log('🔍 Debug: No specific selection, fetching ALL users for SMS...');
            try {
              // Try the search endpoint first as it's more reliable
              const searchResponse = await authorizedFetch('/admin/users/search?q=&limit=500', { method: 'GET' });
              if (searchResponse && Array.isArray(searchResponse.users)) {
                usersToProcess = searchResponse.users;
                console.log(`🔍 Debug: Fetched ${usersToProcess.length} total users for SMS via search endpoint`);
              } else {
                // Fallback to regular users endpoint
                const response = await authorizedFetch('/admin/users?limit=1000&offset=0&cache=false', { method: 'GET' });
                if (response && response.users) {
                  usersToProcess = response.users;
                  console.log(`🔍 Debug: Fetched ${usersToProcess.length} total users for SMS via users endpoint`);
                } else {
                  // Final fallback to current page users
                  usersToProcess = allUsers;
                  console.log('⚠️ Debug: Fallback to current page users');
                }
              }
            } catch (error) {
              console.error('❌ Failed to fetch all users for SMS:', error);
              // Fallback to current page users
              usersToProcess = allUsers;
            }
          }
          
          console.log(`🔍 Debug: selectedUsers.size: ${selectedUsers.size}, allUsers.length: ${allUsers.length}`);
          console.log(`🔍 Debug: selectedUsers:`, Array.from(selectedUsers));
          console.log(`🔍 Debug: usersToProcess.length: ${usersToProcess.length}`);
          
          // Load user profiles to get SMS consent status
          console.log(`🔍 Debug: About to load profiles for ${usersToProcess.length} users:`, usersToProcess.map(u => u.email));
          
          // Check if we need to refresh user data to get phone numbers
          // If all users have empty phone numbers, we might need fresh data from Cognito
          const usersWithEmptyPhones = usersToProcess.filter(user => !user.phone || user.phone.trim() === '');
          if (usersWithEmptyPhones.length > 0) {
            console.log(`🔍 Debug: ${usersWithEmptyPhones.length} users have empty phone numbers, this might be a caching issue`);
          }
          
          const usersWithProfiles = await Promise.all(
            usersToProcess.map(async (user) => {
              try {
                console.log(`🔍 Debug: Loading profile for ${user.email}...`);
                const profile = await getUserProfile(user.email);
                console.log(`🔍 Debug: Profile for ${user.email}:`, profile);
                
                // If user has no phone number, try to get fresh data from Cognito
                let userWithPhone = user;
                if (!user.phone || user.phone.trim() === '') {
                  console.log(`🔍 Debug: User ${user.email} has no phone number, getting fresh data...`);
                  const freshUser = await getFreshUserData(user.email);
                  if (freshUser && freshUser.phone) {
                    userWithPhone = freshUser;
                    console.log(`🔍 Debug: Got fresh phone number for ${user.email}: ${freshUser.phone}`);
                  }
                }
                
                return {
                  ...userWithPhone,
                  profile: profile,
                  smsConsent: profile?.smsConsent || false
                };
              } catch (error) {
                console.error(`❌ Failed to load profile for ${user.email}:`, error);
                // Fallback to Cognito attributes if profile loading fails
                return {
                  ...user,
                  profile: null,
                  smsConsent: user.attributes?.['custom:sms_opt_in'] === 'true' || user.smsOptIn === true
                };
              }
            })
          );
          
          console.log(`🔍 Debug: Users with profiles:`, usersWithProfiles);
          
          // Filter users who have SMS opt-in and phone numbers
          const smsOptedInUsers = usersWithProfiles.filter(user => {
            // Check phone number in multiple possible locations
            const phoneFromUser = user.phone && user.phone.trim() !== '';
            const phoneFromAttributes = user.attributes?.phone_number && user.attributes.phone_number.trim() !== '';
            // Also check if phone number is in the profile data
            const phoneFromProfile = user.profile?.phone && user.profile.phone.trim() !== '';
            const hasPhone = phoneFromUser || phoneFromAttributes || phoneFromProfile;
            
            // Check SMS consent in multiple possible locations
            const smsConsentFromProfile = user.smsConsent === true;
            const smsConsentFromAttributes = user.attributes?.['custom:sms_opt_in'] === 'true';
            const smsConsentFromProfileData = user.profile?.smsConsent === true;
            const smsConsentFromUserData = user.smsOptIn === true;
            
            const hasSmsConsent = smsConsentFromProfile || smsConsentFromAttributes || smsConsentFromProfileData || smsConsentFromUserData;
            
            console.log(`🔍 Debug: User ${user.email}:`, {
              hasPhone,
              hasSmsConsent,
              phoneFromUser,
              phoneFromAttributes,
              phoneFromProfile,
              phone: user.phone,
              phoneNumber: user.attributes?.phone_number,
              smsConsentFromProfile,
              smsConsentFromAttributes,
              smsConsentFromProfileData,
              smsConsentFromUserData,
              smsConsent: user.smsConsent,
              profile: user.profile,
              userObject: user,
              // Additional phone number fields to check
              allUserKeys: Object.keys(user),
              userAttributes: user.attributes,
              userPhoneFields: {
                phone: user.phone,
                phone_number: user.phone_number,
                attributes_phone_number: user.attributes?.phone_number,
                attributes_phone: user.attributes?.phone,
                profile_phone: user.profile?.phone
              }
            });
            
            return hasPhone && hasSmsConsent;
          });
          
          console.log(`🔍 Debug: SMS opted-in users:`, smsOptedInUsers);
          
          setRecipients(smsOptedInUsers);
          setMessage('');
          setSendStatus(null);
          setSendAsMms(false);
          setImageRef(null);
          setImageUrl(null);
          setImageLinkUrl(null);
          setImageName('');
        } catch (error) {
          console.error('Error loading user profiles for SMS:', error);
          setError('Failed to load user SMS preferences');
        } finally {
          setLoadingProfiles(false);
        }
      }
    };
    
    loadUserProfilesAndCheckSmsConsent();
  }, [isOpen, selectedUsers, allUsers]);

  const handleImagePicked = async (file) => {
    if (!file) return;

    if (!isStorageConfigured()) {
      setError('Image uploads are not configured. Set REACT_APP_S3_BUCKET and REACT_APP_IDENTITY_POOL_ID, then redeploy.');
      return;
    }

    if (file.type && !file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }

    // Basic guardrail: keep uploads reasonably sized for SMS-link usage
    const maxBytes = 8 * 1024 * 1024; // 8MB
    if (file.size && file.size > maxBytes) {
      setError('Image is too large (max 8MB). Please resize and try again.');
      return;
    }

    try {
      setError(null);
      setImageUploading(true);
      setImageName(file.name || 'image');

      const ref = await uploadAdminSmsImage(file);
      const url = await getImageUrlFromKey(ref);
      if (!url) throw new Error('Upload succeeded, but could not resolve a public image URL.');

      const key = ref.startsWith('s3:') ? ref.slice(3) : ref;
      const previewUrl = buildSmsPreviewUrlFromKey(key);

      setImageRef(ref);
      setImageUrl(url);
      // We are phasing out SMS-link "picture messages". Only generate the preview-link when sending SMS-only.
      setImageLinkUrl(sendAsMms ? null : (previewUrl || url));
    } catch (e) {
      console.error('Admin SMS image upload failed:', e);
      setImageRef(null);
      setImageUrl(null);
      setImageLinkUrl(null);
      setImageName('');
      setError(e?.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const clearImage = () => {
    setImageRef(null);
    setImageUrl(null);
    setImageLinkUrl(null);
    setImageName('');
  };

  const handleSend = async () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    if (recipients.length === 0) {
      setError('No users with SMS opt-in found in selection');
      return;
    }

    if (sendAsMms && !imageUrl) {
      setError('Please attach an image to send as MMS (or uncheck "Send as MMS").');
      return;
    }

    // Mobile debugging
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const userAgent = navigator.userAgent;
    console.log('🔍 SMS Send Debug:', {
      isMobile,
      userAgent,
      recipientsCount: recipients.length,
      messageLength: message.trim().length,
      timestamp: new Date().toISOString()
    });

    try {
      setIsSending(true);
      setError(null);

      const isMms = !!sendAsMms && !!imageUrl;
      // For MMS deliveries we send the image as media (not a link) to avoid bloating the SMS body.
      // For SMS-only deliveries with an attached image, we include a preview link in the SMS body.
      const formattedMessage = formatAdminBulkMessage({
        message: message.trim(),
        mediaUrl: isMms ? null : (imageLinkUrl || null)
      });
      
      console.log('🔍 SMS Send - About to call sendBulkSms:', {
        recipients: recipients.map(user => ({
          id: user.id,
          email: user.email,
          phone: user.phone,
          name: user.name
        })),
        messageLength: formattedMessage.length,
        source: 'admin'
      });
      
      const result = await sendBulkSms({
        recipients: recipients.map(user => ({
          id: user.id,
          email: user.email,
          phone: user.phone,
          name: user.name
        })),
        message: formattedMessage,
        source: 'admin',
        // mediaUrl should be a direct S3 HTTPS URL for Pinpoint SendMediaMessage (true MMS).
        // IMPORTANT: Only send mediaUrl when admin explicitly selects MMS.
        mediaUrl: isMms ? imageUrl : null,
        // We are phasing out SMS-link "picture messages"; only send mediaLinkUrl when SMS-only.
        mediaLinkUrl: (!isMms && imageLinkUrl) ? imageLinkUrl : null,
        mediaRef: imageRef,
        // Explicitly tell backend whether this is a true-MMS attempt (no link fallback).
        sendAsMms: isMms
      });

      console.log('🔍 SMS Send - Result received:', result);

      setSendStatus({
        success: true,
        // NOTE: sent/failed can be 0, so use nullish coalescing (|| would incorrectly fall back).
        sent: result?.sent ?? recipients.length,
        failed: result?.failed ?? 0,
        messageIds: result?.messageIds ?? []
      });

    } catch (err) {
      console.error('🔍 SMS Send - Error caught:', err);
      setError(err.message || 'Failed to send SMS messages');
      setSendStatus({
        success: false,
        sent: 0,
        failed: recipients.length
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    setMessage('');
    setSendStatus(null);
    setError(null);
    clearImage();
    setSendAsMms(false);
    onClose();
  };

  const getMessageStats = () => {
    const baseMessage = message.trim();
    const includeLink = !sendAsMms && !!imageLinkUrl;
    const finalMessage = baseMessage
      ? formatAdminBulkMessage({ message: baseMessage, mediaUrl: includeLink ? imageLinkUrl : null })
      : '';

    const total = finalMessage.length;
    // Rough estimate; real segmentation depends on encoding (GSM-7 vs UCS-2).
    const segments = total <= 160 ? 1 : Math.ceil(total / 153);

    return { base: baseMessage.length, total, segments, finalMessage };
  };

  const messageStats = getMessageStats();
  const isOver160 = messageStats.total > 160;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="glass-card-dark border border-theme-silver/20 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl backdrop-blur-sm">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-theme-silver/20">
          <div className="flex items-center space-x-3">
            <MessageSquare className="h-6 w-6 text-custom-orange" />
            <h2 className="text-xl font-semibold text-theme-silver">Send SMS Messages</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-theme-silver-light hover:text-theme-silver transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Recipients Info */}
          <div className="glass-card-dark/50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Users className="h-5 w-5 text-custom-orange" />
              <h3 className="font-medium text-theme-silver">Recipients</h3>
            </div>
            <div className="text-sm text-theme-silver-light space-y-1">
              <p>Total selected: {selectedUsers.size > 0 ? selectedUsers.size : allUsers.length} users</p>
              {loadingProfiles ? (
                <p className="text-blue-400 flex items-center space-x-1">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Loading SMS preferences...</span>
                </p>
              ) : (
                <>
                  <p>SMS opted-in: {recipients.length} users</p>
                  {recipients.length === 0 && (
                    <p className="text-yellow-500 flex items-center space-x-1">
                      <AlertCircle className="h-4 w-4" />
                      <span>No users with SMS opt-in found in selection</span>
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Message Composition */}
          <div className="space-y-4">
            <div>
              <label htmlFor="sms-message" className="block text-sm font-medium text-theme-silver mb-2">
                Message Content
              </label>
              <p className="text-xs text-theme-silver-light mb-2">
                Messages will be prefixed with "Tolbert Tactical:" and include compliance text automatically.
              </p>
              <textarea
                id="sms-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your message here..."
                className="w-full h-32 px-3 py-2 bg-theme-black border border-theme-silver/20 rounded-lg text-theme-silver placeholder-theme-silver-light focus:outline-none focus:ring-2 focus:ring-custom-orange focus:border-transparent resize-none"
                disabled={isSending}
              />
            </div>

            {/* Character Count */}
            <div className="flex justify-between items-center text-sm">
              <div className="text-theme-silver-light">
                <span>Message: {messageStats.base} chars</span>
                <span className="mx-2">•</span>
                <span>Total (incl. prefix/compliance/link): {messageStats.total} chars</span>
                <span className="mx-2">•</span>
                <span>Est. segments: {messageStats.finalMessage ? messageStats.segments : 0}</span>
              </div>
              <div className={`font-medium ${isOver160 ? 'text-yellow-500' : 'text-theme-silver-light'}`}>
                {isOver160 ? 'Will send as multiple SMS segments' : 'Fits in a single SMS segment'}
              </div>
            </div>

            {/* Optional Image Attachment (AWS-only: link-in-SMS) */}
            <div className="glass-card-dark/30 rounded-lg p-3 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-theme-silver">Attach Image (optional)</h4>
                  <p className="text-xs text-theme-silver-light">
                    Check “Send as MMS” to attempt a true MMS (media in the message). If unchecked, we’ll include a link in the SMS.
                  </p>
                </div>
                {imageUrl && (
                  <button
                    type="button"
                    onClick={clearImage}
                    className="text-xs text-theme-silver-light hover:text-theme-silver transition-colors"
                    disabled={isSending || imageUploading}
                  >
                    Remove
                  </button>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-theme-silver-light">
                <input
                  type="checkbox"
                  checked={sendAsMms}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setSendAsMms(next);

                    // If switching to MMS, we intentionally drop the SMS preview-link.
                    if (next) {
                      setImageLinkUrl(null);
                      return;
                    }

                    // If switching back to SMS-only and we already have an uploaded image, generate the preview link on-demand.
                    if (!next && imageRef && !imageLinkUrl) {
                      const key = imageRef.startsWith('s3:') ? imageRef.slice(3) : imageRef;
                      setImageLinkUrl(buildSmsPreviewUrlFromKey(key) || imageUrl);
                    }
                  }}
                  disabled={isSending}
                />
                <span className="select-none">Send as MMS (where supported)</span>
              </label>

              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImagePicked(e.target.files?.[0])}
                  disabled={isSending || imageUploading || !isStorageConfigured()}
                  className="block w-full text-sm text-theme-silver-light file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-theme-black file:text-theme-silver file:border file:border-theme-silver/20 hover:file:bg-theme-black/70"
                />
              </div>

              {!isStorageConfigured() && (
                <p className="text-xs text-yellow-500">
                  Image upload is not configured in this environment (missing `REACT_APP_S3_BUCKET` / Identity Pool).
                </p>
              )}

              {imageUploading && (
                <p className="text-xs text-blue-400 flex items-center space-x-2">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Uploading image...</span>
                </p>
              )}

              {imageUrl && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-theme-silver-light truncate">
                      Attached: {imageName || 'image'}
                    </p>
                    <a
                      href={imageLinkUrl || imageUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-custom-orange hover:underline"
                    >
                      Open
                    </a>
                  </div>
                  <div className="bg-theme-black/40 border border-theme-silver/10 rounded-lg overflow-hidden">
                    <img
                      src={imageUrl}
                      alt="Attached"
                      className="w-full max-h-56 object-contain"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Compliance Preview */}
            {message.trim() && (
              <div className="glass-card-dark/30 rounded-lg p-3">
                <h4 className="text-sm font-medium text-theme-silver mb-2">Message Preview:</h4>
                <p className="text-sm text-theme-silver-light break-words">
                  {formatAdminBulkMessage({
                    message: message.trim(),
                    mediaUrl: (!sendAsMms && imageLinkUrl) ? imageLinkUrl : null
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-400 font-medium">Error</span>
              </div>
              <p className="text-red-300 text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Success Display */}
          {sendStatus?.success && (
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-green-400 font-medium">Messages Sent Successfully</span>
              </div>
              <div className="text-green-300 text-sm mt-1 space-y-1">
                <p>Sent: {sendStatus.sent} messages</p>
                {sendStatus.failed > 0 && <p>Failed: {sendStatus.failed} messages</p>}
              </div>
            </div>
          )}

          {/* Failure Display */}
          {sendStatus && !sendStatus.success && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-400 font-medium">Send Failed</span>
              </div>
              <p className="text-red-300 text-sm mt-1">
                Failed to send {sendStatus.failed} messages
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-theme-silver/20">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-theme-silver-light hover:text-theme-silver transition-colors"
            disabled={isSending}
          >
            {sendStatus?.success ? 'Close' : 'Cancel'}
          </button>
          <button
            onClick={handleSend}
            onTouchEnd={(e) => {
              // Prevent double-tap zoom on mobile
              e.preventDefault();
              if (!isSending && !loadingProfiles && recipients.length > 0 && message.trim()) {
                handleSend();
              }
            }}
            disabled={isSending || loadingProfiles || recipients.length === 0 || !message.trim()}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
            style={{ touchAction: 'manipulation' }}
          >
            {isSending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                <span>Send to {recipients.length} Users</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminSmsModal;
